import React from 'react';
import { CheckCircle, XCircle, AlertCircle, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function AuthModal({
  isOpen,
  onClose,
  type = 'success', // 'success', 'error', 'warning', 'info'
  title,
  message,
  showResendButton = false,
  onResend,
  email
}) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'error':
        return <XCircle className="w-16 h-16 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-16 h-16 text-yellow-500" />;
      case 'info':
        return <Mail className="w-16 h-16 text-blue-500" />;
      default:
        return <CheckCircle className="w-16 h-16 text-green-500" />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50';
      case 'error':
        return 'bg-red-50';
      case 'warning':
        return 'bg-yellow-50';
      case 'info':
        return 'bg-blue-50';
      default:
        return 'bg-green-50';
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'success':
        return 'border-green-200';
      case 'error':
        return 'border-red-200';
      case 'warning':
        return 'border-yellow-200';
      case 'info':
        return 'border-blue-200';
      default:
        return 'border-green-200';
    }
  };

  // Replace {email} placeholder with actual email
  const formattedMessage = message?.replace('{email}', email || '');

  // Prevent ANY backdrop clicks from closing the modal
  const handleBackdropClick = (e) => {
    // ONLY close if clicking directly on backdrop, not modal content
    if (e.target === e.currentTarget) {
      e.stopPropagation();
      e.preventDefault();
      // Do NOT call onClose - user MUST click a button
      return;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      onMouseDown={handleBackdropClick}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-fade-in-scale"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Icon Section */}
        <div className={`${getBackgroundColor()} ${getBorderColor()} border-b-2 rounded-t-2xl p-6 flex justify-center`}>
          {getIcon()}
        </div>

        {/* Content Section */}
        <div className="p-6">
          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
            {title}
          </h2>

          {/* Message */}
          <p className="text-gray-600 text-center whitespace-pre-line leading-relaxed">
            {formattedMessage}
          </p>

          {/* Email Display (if provided) */}
          {email && type === 'info' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 text-center font-medium break-all">
                {email}
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="mt-6 flex gap-3">
            {showResendButton && onResend && (
              <button
                onClick={() => {
                  onResend();
                  onClose();
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                {t('auth.resendButton')}
              </button>
            )}

            <button
              onClick={onClose}
              className={`${showResendButton ? 'flex-1' : 'w-full'} ${
                type === 'success'
                  ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                  : type === 'error'
                  ? 'bg-red-600 hover:bg-red-700 active:bg-red-800'
                  : type === 'warning'
                  ? 'bg-yellow-600 hover:bg-yellow-700 active:bg-yellow-800'
                  : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
              } text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200`}
            >
              {showResendButton ? t('auth.cancelButton') : t('auth.okButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
